import { round } from 'lodash';
import { ConsiderationType } from 'further-types/exit';
import type { UseFormSetValue } from 'react-hook-form';
import type { ExitDetailsFormValues } from './index';

type CalculateCashConsiderationValuesParams = {
  changedField: 'perShare' | 'total';
  newValue?: string;
  considerationType: ConsiderationType | undefined;
  sharesToBeSold: number;
  isCalculating: React.MutableRefObject<boolean>;
  setValue: UseFormSetValue<ExitDetailsFormValues>;
  fieldPrefix: 'taxableValue' | 'receivedValue';
};

const calculateCashConsiderationValues = ({
  changedField,
  newValue,
  considerationType,
  sharesToBeSold,
  isCalculating,
  setValue,
  fieldPrefix,
}: CalculateCashConsiderationValuesParams) => {
  if (isCalculating.current) {
    return;
  }

  if (considerationType !== ConsiderationType.Cash) {
    return;
  }

  // Ensure we have shares to be sold
  if (!sharesToBeSold || sharesToBeSold <= 0) {
    return;
  }

  // Check if the changed field value is valid
  const changedValue = Number(newValue) || 0;
  if (changedValue < 0) {
    return;
  }

  isCalculating.current = true;

  try {
    if (changedField === 'total') {
      // Calculate per share: total / shares to be sold
      // Up to 12 decimal places
      const total = Number(newValue) || 0;
      const calculatedPerShare = total / sharesToBeSold;

      // Round to 12 decimal places (removes trailing zeros)
      const rounded = round(calculatedPerShare, 12);

      setValue(
        fieldPrefix === 'taxableValue'
          ? 'taxableValueSharePrice'
          : 'sharePrice',
        rounded.toString(),
      );
    } else if (changedField === 'perShare') {
      // Calculate total: per share * shares to be sold
      // Round to 2 decimal places
      const perShare = Number(newValue) || 0;
      const calculatedTotal = perShare * sharesToBeSold;
      const roundedTotal = round(calculatedTotal, 2);

      setValue(
        fieldPrefix === 'taxableValue'
          ? 'taxableValueTotal'
          : 'receivedValueTotal',
        roundedTotal.toString(),
      );
    }
  } finally {
    isCalculating.current = false;
  }
};

export default calculateCashConsiderationValues;
